import type { UnderwayActivity } from './types';

// Kind-based headings for the underway row (PM copy matrix, 2026-08-10). The
// heading is generic — "Sending email(s)", "Interview scheduled" — while the
// activity NAME lives only in the description sentence. External providers
// collapse onto the core noun (HackerRank → "assessment"), mirroring the
// builder's PARENT_NOUN_BY_KIND.

type HeadingNoun =
  | 'email'
  | 'internal email'
  | 'interview'
  | 'assessment'
  | 'review'
  | 'survey'
  | 'document'
  | 'background check';

const NOUN_BY_KIND: Record<string, HeadingNoun> = {
  email: 'email',
  // Without its own noun this fell through to the default and read "Sending
  // email", which is the candidate-facing activity's heading — the two are
  // separate actions on the same stage and the row has to say which one ran.
  internal_email: 'internal email',
  interview: 'interview',
  hackerrank_interview: 'interview',
  assessment: 'assessment',
  hackerrank: 'assessment',
  hackerearth_assessment: 'assessment',
  criteriacorp_assessment: 'assessment',
  codeinterview_assessment: 'assessment',
  hireflix_assessment: 'assessment',
  review: 'review',
  survey: 'survey',
  // Without this an e-sign row read "Sending email" — the map's old `?? 'email'`
  // default named a different activity with complete confidence (CORE-5409).
  esign: 'document',
  // "Sending background check" / "Background checks queued" (ENG-4228).
  certn_background_check: 'background check',
  zinc_background_check: 'background check',
  springverify_background_check: 'background check',
};

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The row's heading, per the matrix:
 * - immediate, during (pending/paused/running): "Sending email(s)" /
 *   "Scheduling interview(s)"
 * - immediate, after (completed): "Email(s) sent" / "Interview(s) scheduled"
 * - delayed, during: "Queuing email(s)"
 * - delayed, after: "Email(s) queued"
 * Returns null for states the matrix doesn't cover (failed / cancelled), and
 * for a kind with no noun — callers fall back to label-based copy, which names
 * the activity that actually ran. A `target` is omitted entirely when the
 * referenced stage action has been hard-deleted, and guessing "email" there is
 * how an e-sign came to be announced as an email.
 */
export const getUnderwayHeading = (
  activity: Pick<
    UnderwayActivity,
    'activityKind' | 'isBulk' | 'delayed' | 'status' | 'details'
  >
): string | null => {
  const { status } = activity;
  if (status === 'failed' || status === 'cancelled') return null;

  const baseNoun = NOUN_BY_KIND[activity.activityKind];
  if (!baseNoun) return null;
  // Every kind pluralises on the number of CANDIDATES — except e-sign, which
  // pluralises on the number of DOCUMENTS in the envelope (PM, CORE-5409).
  // Three documents to one candidate still reads "documents"; one document to
  // fifty candidates reads "document". An absent count means the payload
  // carried no target, so fall back rather than assert either way.
  const documentCount = activity.details?.documentCount;
  const isPlural =
    activity.activityKind === 'esign' && documentCount !== undefined
      ? documentCount > 1
      : activity.isBulk;
  const noun = isPlural ? `${baseNoun}s` : baseNoun;
  const after = status === 'completed';

  if (activity.delayed) {
    // PM's e-sign copy schedules where every other kind queues (CORE-5409).
    if (activity.activityKind === 'esign') {
      return after ? `${capitalize(noun)} scheduled` : `Scheduling ${noun}`;
    }
    return after ? `${capitalize(noun)} queued` : `Queuing ${noun}`;
  }
  if (baseNoun === 'interview') {
    return after ? `${capitalize(noun)} scheduled` : `Scheduling ${noun}`;
  }
  return after ? `${capitalize(noun)} sent` : `Sending ${noun}`;
};
